import { ref } from 'vue';
import { api } from '@/services/api';

/**
 * Airflow Visualization Composable
 *
 * Handles airflow data visualization including:
 * - AHU tree building and display
 * - Airflow path loading and processing
 * - Space airflow connection data
 * - Airflow table data generation
 *
 * @returns {Object} Airflow visualization state and methods
 */
export function useAirflowVisualization() {
  // ============================================================================
  // STATE
  // ============================================================================

  const loadingAhuTree = ref(false);
  const ahuTreeNodes = ref([]);
  const selectedAhu = ref(null);
  const selectedAhuName = ref('');
  const spaceAirflowData = ref({});
  const airflowData = ref(null);
  const loadingAirflow = ref(false);
  const airflowTableData = ref([]);

  // ============================================================================
  // AHU TREE OPERATIONS
  // ============================================================================

  /**
   * Load and build AHU tree structure grouped by level
   *
   * @param {Array} equipmentList - Full equipment list
   * @param {Function} loadEquipmentList - Function to load equipment if needed
   * @returns {Promise<void>}
   */
  async function loadAhuTree(equipmentList, loadEquipmentList) {
    loadingAhuTree.value = true;
    try {
      // Try to load equipment if not already loaded
      if (!equipmentList || equipmentList.length === 0) {
        try {
          await loadEquipmentList();
        } catch (error) {
          // Continue anyway in case we have some data
        }
      }

      // If still no equipment, return empty
      if (!equipmentList || equipmentList.length === 0) {
        ahuTreeNodes.value = [];
        return;
      }

      // Filter equipment for AHUs - be very inclusive in the search
      const ahus = equipmentList.filter((eq) => {
        const brickClass = eq.brick_class || '';
        return brickClass.includes('Air_Handling_Unit') ||
          brickClass.includes('Air_Handler_Unit') ||
          brickClass.includes('AHU') ||
          eq.name?.includes('AHU');
      });

      // If no AHUs found, show empty state
      if (ahus.length === 0) {
        ahuTreeNodes.value = [];
        return;
      }

      // Create simple tree structure grouped by level
      const byLevel = {};
      ahus.forEach((ahu) => {
        const levelId = ahu.building_level_id || 'unassigned';
        if (!byLevel[levelId]) {
          byLevel[levelId] = [];
        }
        byLevel[levelId].push(ahu);
      });

      // Build the tree
      const tree = [];
      Object.keys(byLevel).sort().forEach((levelId) => {
        if (levelId === 'unassigned') {
          // Add unassigned AHUs directly to root
          byLevel[levelId].forEach((ahu) => {
            tree.push({
              id: `ahu-${ahu.id}`,
              label: ahu.name || `AHU ${ahu.id}`,
              icon: 'air',
              selectable: true,
              data: ahu,
            });
          });
        } else {
          // Group by level
          const levelName = `Level ${levelId}`;
          const levelNode = {
            id: `level-${levelId}`,
            label: levelName,
            icon: 'layers',
            children: byLevel[levelId].map((ahu) => ({
              id: `ahu-${ahu.id}`,
              label: ahu.name || `AHU ${ahu.id}`,
              icon: 'air',
              selectable: true,
              data: ahu,
            })),
          };
          tree.push(levelNode);
        }
      });

      ahuTreeNodes.value = tree;
    } catch (error) {
      // Don't show error notification, just log it
      ahuTreeNodes.value = [];
    } finally {
      loadingAhuTree.value = false;
    }
  }

  /**
   * Handle AHU selection and load airflow data
   *
   * @param {string} nodeId - Selected AHU node ID
   * @param {Function} findAhuNode - Function to find AHU node by ID
   * @param {string} siteId - Site ID
   * @param {Function} notifyError - Error notification function
   * @returns {Promise<void>}
   */
  async function onAhuSelected(nodeId, findAhuNode, siteId, notifyError) {
    if (!nodeId) {
      selectedAhuName.value = '';
      airflowData.value = null;
      return;
    }

    // Find the selected AHU node to get its name and data
    const ahuNode = findAhuNode(nodeId);
    if (!ahuNode) {
      return;
    }

    selectedAhuName.value = ahuNode.label;

    if (!nodeId.startsWith('ahu-')) {
      return;
    }

    // Extract the actual AHU ID from the node ID
    const ahuId = nodeId.replace('ahu-', '');

    loadingAirflow.value = true;
    try {
      // Load airflow paths for this AHU
      const response = await api.get(`/sites/${siteId}/airflow/${ahuId}`);

      if (response.data) {
        airflowData.value = response.data;
      } else {
        airflowData.value = null;
      }
    } catch (error) {
      if (notifyError) {
        notifyError('Failed to load airflow data for selected AHU');
      }
      airflowData.value = null;
    } finally {
      loadingAirflow.value = false;
    }
  }

  // ============================================================================
  // SPACE AIRFLOW OPERATIONS
  // ============================================================================

  /**
   * Load airflow data for all spaces in the site
   *
   * @param {string} siteId - Site ID
   * @returns {Promise<void>}
   */
  async function loadSpaceAirflowData(siteId) {
    if (!siteId) {
      return;
    }

    try {
      const apiUrl = `/sites/${siteId}/airflow/connections`;

      // Fetch dynamic airflow connections for all spaces
      const response = await api.get(apiUrl);

      if (response.data && response.data.spaces) {
        // Store airflow data indexed by space ID
        const airflowMap = {};

        response.data.spaces.forEach((space) => {
          if (space.space_id && space.connections && space.connections.length > 0) {
            // Format the airflow supply string
            const supplyStrings = space.connections.map((conn) => {
              const ahuName = conn.ahu_name || 'AHU';
              const zones = conn.zones.map((z) => z.name).join(', ');
              return `${ahuName} → ${zones}`;
            });

            airflowMap[space.space_id] = supplyStrings.join('; ');
          }
        });

        spaceAirflowData.value = airflowMap;
      }
    } catch (error) {
      // Don't show error to user - airflow data is supplementary
    }
  }

  /**
   * Process airflow data for table display
   *
   * @param {Object} data - Raw airflow data
   * @returns {void}
   */
  function processAirflowDataForTable(data) {
    if (!data || !data.paths) {
      airflowTableData.value = [];
      return;
    }

    const tableData = [];

    // Process each path
    data.paths.forEach((path, pathIndex) => {
      // Get AHU info
      const ahuName = path.ahu?.name || 'Unknown AHU';
      const ahuId = path.ahu?.id || null;

      // Process zones in this path
      if (path.zones && Array.isArray(path.zones)) {
        path.zones.forEach((zone, zoneIndex) => {
          const zoneName = zone.name || `Zone ${zoneIndex + 1}`;
          const zoneId = zone.id || null;

          // Process spaces served by this zone
          if (zone.spaces && Array.isArray(zone.spaces)) {
            zone.spaces.forEach((space, spaceIndex) => {
              tableData.push({
                id: `${pathIndex}-${zoneIndex}-${spaceIndex}`,
                ahuName,
                ahuId,
                zoneName,
                zoneId,
                spaceName: space.name || `Space ${spaceIndex + 1}`,
                spaceId: space.id || null,
                spaceType: space.brick_class ? space.brick_class.replace('brick:', '') : 'Unknown',
                cfm: space.design_cfm || '-',
                flowPath: `${ahuName} → ${zoneName} → ${space.name}`,
              });
            });
          } else {
            // Zone with no spaces
            tableData.push({
              id: `${pathIndex}-${zoneIndex}`,
              ahuName,
              ahuId,
              zoneName,
              zoneId,
              spaceName: '-',
              spaceId: null,
              spaceType: '-',
              cfm: '-',
              flowPath: `${ahuName} → ${zoneName}`,
            });
          }
        });
      } else {
        // AHU with no zones
        tableData.push({
          id: `${pathIndex}`,
          ahuName,
          ahuId,
          zoneName: '-',
          zoneId: null,
          spaceName: '-',
          spaceId: null,
          spaceType: '-',
          cfm: '-',
          flowPath: ahuName,
        });
      }
    });

    airflowTableData.value = tableData;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Reset airflow visualization state
   */
  function resetAirflowState() {
    selectedAhu.value = null;
    selectedAhuName.value = '';
    airflowData.value = null;
    airflowTableData.value = [];
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    loadingAhuTree,
    ahuTreeNodes,
    selectedAhu,
    selectedAhuName,
    spaceAirflowData,
    airflowData,
    loadingAirflow,
    airflowTableData,

    // Methods
    loadAhuTree,
    onAhuSelected,
    loadSpaceAirflowData,
    processAirflowDataForTable,
    resetAirflowState,
  };
}
